import React, { useState, useEffect, useMemo, useCallback, useTransition } from 'react';
import { authFetch } from '../services/authFetch';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Autocomplete,
  Checkbox,
  ListItemText,
  TextField,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  SelectChangeEvent,
  Collapse,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Refresh,
  Clear,
  DateRange,
  InfoOutlined,
  FileDownload,
} from '@mui/icons-material';
import * as XLSX from 'xlsx-js-style';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AssetChangeRecord {
  portfolio: string;
  platform: string;
  period_type: 'WEEK' | 'MONTH' | 'QUARTER';
  period_value: string;
  app_certification_pct: number | null;
  mvc_certification_pct: number | null;
  total_mvc_count: number | null;
  onboarded_mvc_count: number | null;
  change_visibility_pct: number | null;
  red_channel_count: number | null;
  // Portfolio-level fields
  app_certification_pct_portfolio: number | null;
  mvc_certification_pct_portfolio: number | null;
  total_mvc_count_portfolio: number | null;
  onboarded_mvc_count_portfolio: number | null;
  change_visibility_pct_portfolio: number | null;
  red_channel_count_portfolio: number | null;
  // Restore and Failover test fields (tab removed – kept for reference)
  // restore_test_count_last_12_months: number | null;
  // failover_test_count_last_12_months: number | null;
  // restore_test_pct_last_12_months: number | null;
  // failover_test_pct_last_12_months: number | null;
  // restore_test_count_last_12_months_portfolio: number | null;
  // failover_test_count_last_12_months_portfolio: number | null;
  // restore_test_pct_last_12_months_portfolio: number | null;
  // failover_test_pct_last_12_months_portfolio: number | null;
}

interface P1P2Record {
  period_type: 'WEEK' | 'MONTH' | 'QUARTER';
  period_value: string;
  total_p1p2: number;
  p1_p2_caused_by_change_count: number;
  p1_p2_caused_by_change_percentage: number;
  p1_p2_detected_by_alerts_count: number;
  p1_p2_detected_by_alerts_percentage: number;
}

interface ApiResponse {
  status: number;
  data: AssetChangeRecord[];
}

interface P1P2ApiResponse {
  status: number;
  data: P1P2Record[];
}

type MetricKey = 
  | 'app_certification_pct' 
  | 'mvc_certification_pct' 
  | 'change_visibility_pct' 
  | 'red_channel_count'
  // | 'restore_test_pct_last_12_months'  // restore/failover tab removed
  // | 'failover_test_pct_last_12_months'
  | 'p1_p2_caused_by_change_percentage'
  | 'p1_p2_detected_by_alerts_percentage';

type AssetChangeMetricKey = 
  | 'app_certification_pct' 
  | 'mvc_certification_pct' 
  | 'change_visibility_pct' 
  | 'red_channel_count';
  // | 'restore_test_pct_last_12_months'  // restore/failover tab removed
  // | 'failover_test_pct_last_12_months'

interface MetricConfig {
  key: MetricKey;
  label: string;
  target: number;
  higherIsBetter: boolean;
  formatValue: (v: number | null, count?: number | null, total?: number | null) => string;
  isGreen: (v: number | null) => boolean;
  isRed: (v: number | null) => boolean;
}

const CERTIFICATION_METRICS: MetricConfig[] = [
  {
    key: 'app_certification_pct',
    label: 'MAC Apps Certified in MSA',
    target: 90,
    higherIsBetter: true,
    formatValue: (v) => v !== null ? `${v.toFixed(0)}%` : 'N/A',
    isGreen: (v) => v !== null && v >= 90,
    isRed: (v) => v !== null && v < 90,
  },
  {
    key: 'mvc_certification_pct',
    label: 'MVC Apps Certified in MSA',
    target: 100,
    higherIsBetter: true,
    formatValue: (v) => v !== null ? `${v.toFixed(0)}%` : 'N/A',
    isGreen: (v) => v !== null && v >= 100,
    isRed: (v) => v !== null && v < 100,
  },
  {
    key: 'change_visibility_pct',
    label: 'MVC Apps Change Visibility',
    target: 100,
    higherIsBetter: true,
    formatValue: (v, count, total) => {
      if (v === null) return 'N/A';
      if (count !== null && count !== undefined && total !== null && total !== undefined) {
        return `${v.toFixed(0)}% (${count}/${total})`;
      }
      return `${v.toFixed(0)}%`;
    },
    isGreen: (v) => v !== null && v >= 100,
    isRed: (v) => v !== null && v < 100,
  },
  {
    key: 'red_channel_count',
    label: 'Apps in Red Channel',
    target: 0,
    higherIsBetter: false,
    formatValue: (v) => v !== null ? `${v}` : 'N/A',
    isGreen: (v) => (v ?? 0) === 0,
    isRed: (v) => (v ?? 0) > 0,
  },
];

/* Restore and Failover Test tab removed – keeping metric config for reference:
const RESTORE_FAILOVER_METRICS: MetricConfig[] = [
  {
    key: 'restore_test_pct_last_12_months',
    label: 'Apps with Restore Test performed in last 12 months',
    target: 90,
    higherIsBetter: true,
    formatValue: (v, count, total) => {
      if (v === null) return 'N/A';
      if (count !== null && count !== undefined && total !== null && total !== undefined) {
        return `${v.toFixed(0)}% (${count}/${total})`;
      }
      return `${v.toFixed(0)}%`;
    },
    isGreen: (v) => v !== null && v >= 90,
    isRed: (v) => v !== null && v < 90,
  },
  {
    key: 'failover_test_pct_last_12_months',
    label: 'Apps with Failover Test performed in last 12 months',
    target: 90,
    higherIsBetter: true,
    formatValue: (v, count, total) => {
      if (v === null) return 'N/A';
      if (count !== null && count !== undefined && total !== null && total !== undefined) {
        return `${v.toFixed(0)}% (${count}/${total})`;
      }
      return `${v.toFixed(0)}%`;
    },
    isGreen: (v) => v !== null && v >= 90,
    isRed: (v) => v !== null && v < 90,
  },
];
*/

const P1P2_METRICS: MetricConfig[] = [
  {
    key: 'p1_p2_caused_by_change_percentage',
    label: 'Major Incidents Caused by Change',
    target: 20,
    higherIsBetter: false,
    formatValue: (v, count, total) => {
      if (v === null) return 'N/A';
      if (count !== null && count !== undefined && total !== null && total !== undefined) {
        return `${v.toFixed(1)}% (${count}/${total})`;
      }
      return `${v.toFixed(1)}%`;
    },
    isGreen: (v) => v !== null && v <= 20,
    isRed: (v) => v !== null && v > 20,
  },
  {
    key: 'p1_p2_detected_by_alerts_percentage',
    label: 'Major Incidents Detected by Alerts',
    target: 30,
    higherIsBetter: true,
    formatValue: (v, count, total) => {
      if (v === null) return 'N/A';
      if (count !== null && count !== undefined && total !== null && total !== undefined) {
        return `${v.toFixed(1)}% (${count}/${total})`;
      }
      return `${v.toFixed(1)}%`;
    },
    isGreen: (v) => v !== null && v >= 30,
    isRed: (v) => v !== null && v < 30,
  },
];

const SELECT_ALL = 'Select All';
const PERIOD_TYPE_LABELS: Record<string, string> = {
  WEEK: 'Weekly',
  MONTH: 'Monthly',
  QUARTER: 'Quarterly',
};

// ─── Period comparison helpers ────────────────────────────────────────────────
// Convert a period_value string into a comparable number so we can do >= / <=.
// WEEK: 2026-W12 → 202612, MONTH: 2026-03 → 202603, QUARTER: 2026-Q1 → 20261
const periodToNum = (value: string): number => {
  if (value.includes('-W')) {
    const [yr, wk] = value.split('-W');
    return parseInt(yr) * 100 + parseInt(wk);
  }
  if (value.includes('-Q')) {
    const [yr, q] = value.split('-Q');
    return parseInt(yr) * 10 + parseInt(q);
  }
  // MONTH: 2026-03
  const [yr, mo] = value.split('-');
  return parseInt(yr) * 100 + parseInt(mo);
};

// Given a period_type and a lookback of N weeks/months/quarters, return the
// string period_value for (today − N).
const periodNPeriodsAgo = (type: 'WEEK' | 'MONTH' | 'QUARTER', n: number): string => {
  const now = new Date();
  if (type === 'WEEK') {
    now.setDate(now.getDate() - n * 7);
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
  }
  if (type === 'MONTH') {
    now.setMonth(now.getMonth() - n);
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }
  // QUARTER
  const totalQuarters = (now.getFullYear() * 4 + Math.floor(now.getMonth() / 3)) - n;
  const yr = Math.floor(totalQuarters / 4);
  const q = (totalQuarters % 4) + 1;
  return `${yr}-Q${q}`;
};

// Current period string (period containing today)
const currentPeriod = (type: 'WEEK' | 'MONTH' | 'QUARTER'): string => {
  return periodNPeriodsAgo(type, 0);
};

// YTD start period for the given type
const ytdStartPeriod = (type: 'WEEK' | 'MONTH' | 'QUARTER'): string => {
  const yr = new Date().getFullYear();
  if (type === 'WEEK') return `${yr}-W01`;
  if (type === 'MONTH') return `${yr}-01`;
  return `${yr}-Q1`;
};

// ─── Helper to format period label ───────────────────────────────────────────
const formatPeriodLabel = (value: string, type: string): string => {
  if (type === 'WEEK') {
    const [yr, wk] = value.split('-W');
    return `${yr} W${wk}`;
  }
  if (type === 'MONTH') {
    const [yr, mo] = value.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(mo) - 1]} ${yr}`;
  }
  if (type === 'QUARTER') {
    const [yr, q] = value.split('-Q');
    return `${yr} Q${q}`;
  }
  return value;
};

// ─── Component ────────────────────────────────────────────────────────────────
const AssetChangeMetrics: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AssetChangeRecord[]>([]);
  const [p1p2Data, setP1p2Data] = useState<P1P2Record[]>([]);

  // Refs for synchronized scrolling
  const topScrollRef = React.useRef<HTMLDivElement>(null);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const cleanupRef = React.useRef<(() => void) | null>(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);

  // Tab state
  // restore-failover tab removed; original type: 'certification' | 'restore-failover' | 'p1-p2'
  const [selectedTab, setSelectedTab] = useState<'certification' | 'p1-p2'>('certification');

  // Filters
  const [periodType, setPeriodType] = useState<'WEEK' | 'MONTH' | 'QUARTER'>('WEEK');
  const [selectedPortfolios, setSelectedPortfolios] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [viewBy, setViewBy] = useState<'portfolio' | 'platform'>('portfolio');
  const [isPending, startTransition] = useTransition();
  // Initialize with certification metrics since that's the default tab
  const [selectedMetricKeys, setSelectedMetricKeys] = useState<MetricKey[]>(
    CERTIFICATION_METRICS.map(m => m.key)
  );

  // Time range filter (quick presets + custom)
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('ytd');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customPeriods, setCustomPeriods] = useState<string[]>([]);
  // Resolved start/end period_values ('' means no bound)
  const [filterStartPeriod, setFilterStartPeriod] = useState<string>('');
  const [filterEndPeriod, setFilterEndPeriod] = useState<string>('');

  // Drill-down
  const [expandedPortfolios, setExpandedPortfolios] = useState<Set<string>>(new Set());

  // ─── Fetch ─────────────────────────────────────────────────────────────────
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [assetChangeRes, p1p2Res] = await Promise.all([
        authFetch(`${process.env.REACT_APP_NOC_DASHBOARD_API}/service-deck/asset-change-metrics`),
        authFetch(`${process.env.REACT_APP_NOC_DASHBOARD_API}/service-deck/p1-p2-metrics`),
      ]);
      
      if (!assetChangeRes.ok) throw new Error(`HTTP ${assetChangeRes.status}`);
      if (!p1p2Res.ok) throw new Error(`HTTP ${p1p2Res.status}`);
      
      const assetChangeJson: ApiResponse = await assetChangeRes.json();
      const p1p2Json: P1P2ApiResponse = await p1p2Res.json();
      
      setData(assetChangeJson.data || []);
      setP1p2Data(p1p2Json.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Update scroll width and detect if horizontal scrolling is needed
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    
    if (!tableContainer) return;

    const updateScrollWidth = () => {
      const tableElement = tableContainer.querySelector('table');
      if (tableElement) {
        // Use table's scrollWidth for the inner content
        setTableScrollWidth(tableElement.scrollWidth);
        // Match the container width (excludes vertical scrollbar)
        setContainerWidth(tableContainer.clientWidth);
      }
      setHasHorizontalScroll(tableContainer.scrollWidth > tableContainer.clientWidth);
    };
    
    updateScrollWidth();

    // Use ResizeObserver to update width when table size changes
    const resizeObserver = new ResizeObserver(updateScrollWidth);
    const tableElement = tableContainer.querySelector('table');
    if (tableElement) {
      resizeObserver.observe(tableElement);
    }
    resizeObserver.observe(tableContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, [data, p1p2Data, selectedTab, viewBy, expandedPortfolios, selectedMetricKeys]);

  // Sync scroll between top and bottom scrollbars
  useEffect(() => {
    // Cleanup any previous listeners
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    if (!hasHorizontalScroll) return;

    // Small delay to ensure DOM is rendered
    const timeoutId = setTimeout(() => {
      const topScroll = topScrollRef.current;
      const tableContainer = tableContainerRef.current;
      
      if (!tableContainer || !topScroll) return;

      const handleTopScroll = () => {
        if (!tableContainer) return;
        tableContainer.scrollLeft = topScroll.scrollLeft;
      };

      const handleTableScroll = () => {
        if (!topScroll) return;
        topScroll.scrollLeft = tableContainer.scrollLeft;
      };

      topScroll.addEventListener('scroll', handleTopScroll);
      tableContainer.addEventListener('scroll', handleTableScroll);

      // Store cleanup function
      cleanupRef.current = () => {
        topScroll.removeEventListener('scroll', handleTopScroll);
        tableContainer.removeEventListener('scroll', handleTableScroll);
      };
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [hasHorizontalScroll, tableScrollWidth]);

  // Select metrics based on tab
  const ALL_TAB_METRICS = useMemo(() => 
    selectedTab === 'certification' 
      ? CERTIFICATION_METRICS 
      : P1P2_METRICS,
    [selectedTab]
  );

  // Filter metrics based on selection
  const METRICS = useMemo(() => {
    if (selectedMetricKeys.length === 0) return ALL_TAB_METRICS;
    return ALL_TAB_METRICS.filter(m => selectedMetricKeys.includes(m.key));
  }, [selectedMetricKeys, ALL_TAB_METRICS]);

  // ─── Time range effect ────────────────────────────────────────────────────
  useEffect(() => {
    if (selectedTimeRange === 'all') {
      setFilterStartPeriod('');
      setFilterEndPeriod('');
      setShowCustomPicker(false);
      setCustomPeriods([]);
      return;
    }
    if (selectedTimeRange === 'custom') {
      setShowCustomPicker(true);
      // Custom periods will be applied via Autocomplete onChange
      return;
    }
    setShowCustomPicker(false);
    setCustomPeriods([]);
    const end = currentPeriod(periodType);
    let start = '';
    if (selectedTimeRange === 'ytd') {
      start = ytdStartPeriod(periodType);
    } else if (selectedTimeRange === '3') {
      start = periodNPeriodsAgo(periodType, 2);
    } else if (selectedTimeRange === '6') {
      start = periodNPeriodsAgo(periodType, 5);
    } else if (selectedTimeRange === '12') {
      start = periodNPeriodsAgo(periodType, 11);
    }
    setFilterStartPeriod(start);
    setFilterEndPeriod(end);
  }, [selectedTimeRange, periodType]);

  // ─── Derived filter options ────────────────────────────────────────────────
  const allPortfolios = useMemo(
    () => Array.from(new Set(data.filter(d => d.period_type === periodType).map(d => d.portfolio))).sort(),
    [data, periodType],
  );

  const allPlatforms = useMemo(() => {
    let filtered = data.filter(d => d.period_type === periodType && d.platform && d.platform.trim() !== '');
    if (selectedPortfolios.length > 0) filtered = filtered.filter(d => selectedPortfolios.includes(d.portfolio));
    return Array.from(new Set(filtered.map(d => d.platform))).sort();
  }, [data, periodType, selectedPortfolios]);

  // ─── Filtered data ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let result = data.filter(d => d.period_type === periodType);
    if (filterStartPeriod) {
      const startNum = periodToNum(filterStartPeriod);
      result = result.filter(d => periodToNum(d.period_value) >= startNum);
    }
    if (filterEndPeriod) {
      const endNum = periodToNum(filterEndPeriod);
      result = result.filter(d => periodToNum(d.period_value) <= endNum);
    }
    if (selectedPortfolios.length > 0) result = result.filter(d => selectedPortfolios.includes(d.portfolio));
    if (selectedPlatforms.length > 0) result = result.filter(d => selectedPlatforms.includes(d.platform));
    return result;
  }, [data, periodType, filterStartPeriod, filterEndPeriod, selectedPortfolios, selectedPlatforms]);

  // ─── Sorted periods (reversed - latest rightmost) ────────────────────────
  const periods = useMemo(
    () => Array.from(new Set(filtered.map(d => d.period_value))).sort().reverse(),
    [filtered],
  );

  // All available periods (for dropdown options, not filtered by time range)
  const allAvailablePeriods = useMemo(
    () => Array.from(new Set(data.filter(d => d.period_type === periodType).map(d => d.period_value))).sort(),
    [data, periodType],
  );

  // ─── P1/P2 filtered data ──────────────────────────────────────────────────
  const filteredP1P2 = useMemo(() => {
    let result = p1p2Data.filter(d => d.period_type === periodType);
    if (filterStartPeriod) {
      const startNum = periodToNum(filterStartPeriod);
      result = result.filter(d => periodToNum(d.period_value) >= startNum);
    }
    if (filterEndPeriod) {
      const endNum = periodToNum(filterEndPeriod);
      result = result.filter(d => periodToNum(d.period_value) <= endNum);
    }
    return result;
  }, [p1p2Data, periodType, filterStartPeriod, filterEndPeriod]);

  const p1p2Periods = useMemo(
    () => Array.from(new Set(filteredP1P2.map(d => d.period_value))).sort().reverse(),
    [filteredP1P2],
  );

  const allAvailableP1P2Periods = useMemo(
    () => Array.from(new Set(p1p2Data.filter(d => d.period_type === periodType).map(d => d.period_value))).sort(),
    [p1p2Data, periodType],
  );

  const p1p2Lookup = useMemo(() => {
    const m = new Map<string, P1P2Record>();
    filteredP1P2.forEach(d => {
      m.set(d.period_value, d);
    });
    return m;
  }, [filteredP1P2]);

  // ─── Portfolio→Platform hierarchy ─────────────────────────────────────────
  const portfolios = useMemo(() => {
    const map = new Map<string, Set<string>>();
    filtered.forEach(d => {
      if (!map.has(d.portfolio)) map.set(d.portfolio, new Set());
      // Only add platform if it's not empty/null
      if (d.platform && d.platform.trim() !== '') {
        map.get(d.portfolio)!.add(d.platform);
      }
    });
    return Array.from(map.entries())
      .map(([p, platforms]) => ({ portfolio: p, platforms: Array.from(platforms).sort() }))
      .sort((a, b) => a.portfolio.localeCompare(b.portfolio));
  }, [filtered]);

  // ─── All unique platforms (for platform-only view) ───────────────────────
  const allUniquePlatforms = useMemo(() => {
    const platformSet = new Set<string>();
    filtered.forEach(d => {
      if (d.platform && d.platform.trim() !== '') {
        platformSet.add(d.platform);
      }
    });
    return Array.from(platformSet).sort();
  }, [filtered]);

  // ─── Lookup by portfolio+platform+period ──────────────────────────────────
  const lookup = useMemo(() => {
    const m = new Map<string, AssetChangeRecord>();
    filtered.forEach(d => {
      m.set(`${d.portfolio}|${d.platform}|${d.period_value}`, d);
    });
    return m;
  }, [filtered]);

  // Portfolio aggregate: use portfolio-level fields directly from data
  const portfolioData = useMemo(() => {
    const map = new Map<string, Map<string, AssetChangeRecord>>();
    filtered.forEach(d => {
      if (!map.has(d.portfolio)) map.set(d.portfolio, new Map());
      const byPeriod = map.get(d.portfolio)!;
      // Store the first record for each portfolio/period (they all have same portfolio-level values)
      if (!byPeriod.has(d.period_value)) {
        byPeriod.set(d.period_value, d);
      }
    });
    return map;
  }, [filtered]);

  const getPortfolioMetric = (portfolio: string, period: string, metricKey: MetricKey): { value: number | null; count?: number | null; total?: number | null } => {
    const record = portfolioData.get(portfolio)?.get(period);
    if (!record) return { value: null };

    // Map metric keys to their portfolio-level equivalents
    if (metricKey === 'app_certification_pct') {
      return { value: record.app_certification_pct_portfolio };
    } else if (metricKey === 'mvc_certification_pct') {
      return { value: record.mvc_certification_pct_portfolio };
    } else if (metricKey === 'change_visibility_pct') {
      return { 
        value: record.change_visibility_pct_portfolio,
        count: record.onboarded_mvc_count_portfolio,
        total: record.total_mvc_count_portfolio
      };
    } else if (metricKey === 'red_channel_count') {
      return { value: record.red_channel_count_portfolio };
    }
    /* restore/failover tab removed:
    } else if (metricKey === 'restore_test_pct_last_12_months') {
      return {
        value: record.restore_test_pct_last_12_months_portfolio,
        count: record.restore_test_count_last_12_months_portfolio,
        total: record.total_mvc_count_portfolio
      };
    } else if (metricKey === 'failover_test_pct_last_12_months') {
      return {
        value: record.failover_test_pct_last_12_months_portfolio,
        count: record.failover_test_count_last_12_months_portfolio,
        total: record.total_mvc_count_portfolio
      };
    }
    */
    return { value: null };
  };

  // ─── Cell colour ──────────────────────────────────────────────────────────
  const getCellBg = (metric: MetricConfig, value: number | null): string => {
    if (value === null) return 'transparent';
    if (metric.isGreen(value)) return '#7FD175';
    if (metric.isRed(value)) return '#B80012';
    return '#ff9800';
  };

  // ─── Trend arrow ──────────────────────────────────────────────────────────
  const renderTrendArrow = (
    currentVal: number | null,
    prevVal: number | null,
    higherIsBetter: boolean,
  ): React.ReactNode => {
    if (currentVal === null || prevVal === null || currentVal === prevVal) return null;
    const isUp = currentVal > prevVal;
    const isGoodTrend = isUp === higherIsBetter;
    const arrowColor = isGoodTrend ? '#00c853' : '#ff1744';
    return (
      <span style={{ 
        color: arrowColor, 
        fontSize: '1.1em', 
        marginLeft: 4, 
        fontWeight: 900,
        WebkitTextStroke: '1px black',
        paintOrder: 'stroke fill',
        filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.3))',
      } as React.CSSProperties}>
        {isUp ? '⬆' : '⬇'}
      </span>
    );
  };

  // ─── Toggle portfolio expansion ───────────────────────────────────────────
  const togglePortfolio = (p: string) => {
    setExpandedPortfolios(prev => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
  };

  // ─── Autocomplete helper ──────────────────────────────────────────────────
  const makeSelectAll = (
    label: string,
    options: string[],
    selected: string[],
    setSelected: (v: string[]) => void,
    onClearDownstream?: () => void,
  ) => {
    const allOpts = [SELECT_ALL, ...options];
    const isAll = options.length > 0 && selected.length === options.length;
    return (
      <Autocomplete
        multiple
        size="small"
        limitTags={1}
        options={allOpts}
        value={isAll ? [SELECT_ALL, ...selected] : selected}
        onChange={(_, val) => {
          const hasAll = val.includes(SELECT_ALL);
          if (hasAll && !isAll) { setSelected([...options]); }
          else if (!hasAll && isAll) { setSelected([]); }
          else { setSelected(val.filter(v => v !== SELECT_ALL)); }
          onClearDownstream?.();
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            sx={{
              '& .MuiInputBase-root': { fontSize: '0.8rem', minHeight: 36 },
              '& .MuiInputLabel-root': { fontSize: '0.8rem' },
            }}
          />
        )}
        renderOption={(props, option, { selected: sel }) => (
          <li {...props} style={{ fontWeight: option === SELECT_ALL ? 600 : 'normal' }}>
            <Checkbox checked={option === SELECT_ALL ? isAll : sel} size="small" />
            <ListItemText primary={option} sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' } }} />
          </li>
        )}
        renderTags={(vals, getTagProps) => {
          const filtered = vals.filter(v => v !== SELECT_ALL);
          if (isAll) return <Chip {...getTagProps({ index: 0 })} label="All" size="small" sx={{ fontSize: '0.7rem', height: 20 }} />;
          if (!filtered.length) return null;
          return (
            <>
              <Chip {...getTagProps({ index: 0 })} label={filtered[0]} size="small" sx={{ fontSize: '0.7rem', height: 20, maxWidth: 80 }} />
              {filtered.length > 1 && <Typography variant="caption" sx={{ ml: 0.5, fontSize: '0.7rem' }}>+{filtered.length - 1}</Typography>}
            </>
          );
        }}
        sx={{ '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' } }}
      />
    );
  };

  // ─── Clear filters ─────────────────────────────────────────────────────────
  const clearFilters = () => {
    setSelectedPortfolios([]);
    setSelectedPlatforms([]);
    setExpandedPortfolios(new Set());
    setSelectedTimeRange('ytd');
    setShowCustomPicker(false);
    setCustomPeriods([]);
    setViewBy('portfolio');
    setSelectedMetricKeys(ALL_TAB_METRICS.map(m => m.key));
  };

  // Quick range label helpers
  const QUICK_RANGES: { value: string; label: string }[] = [
    { value: '3', label: periodType === 'WEEK' ? 'Last 3W' : periodType === 'MONTH' ? 'Last 3M' : 'Last 3Q' },
    { value: '6', label: periodType === 'WEEK' ? 'Last 6W' : periodType === 'MONTH' ? 'Last 6M' : 'Last 6Q' },
    { value: '12', label: periodType === 'WEEK' ? 'Last 12W' : periodType === 'MONTH' ? 'Last 12M' : 'Last 12Q' },
    { value: 'ytd', label: 'YTD' },
    { value: 'all', label: 'All' },
    { value: 'custom', label: 'Custom' },
  ];

  // ─── Sticky column widths ─────────────────────────────────────────────────
  const COL_ENTITY = 220;
  const COL_METRIC = 180;
  const COL_TYPE = 80;
  const FROZEN_COL_SHADOW = '3px 0 6px -2px rgba(0,0,0,0.15)';
  
  // Border styles - all black
  const BORDER_LIGHT = '1px solid #000000';  // Thin border between target/actual
  const BORDER_MEDIUM = '1.5px solid #000000'; // Between period columns
  const BORDER_DARK = '2px solid #000000 !important';   // Thick border between portfolios/platforms/metrics

  // ─── Render rows for portfolio level ──────────────────────────────────────
  const renderPortfolioRows = (): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];

    portfolios.forEach(({ portfolio, platforms }) => {
      const isExpanded = expandedPortfolios.has(portfolio);

      METRICS.forEach((metric, mIdx) => {
        const isFirstMetric = mIdx === 0;
        const isLastMetric = mIdx === METRICS.length - 1;

        // Target row (first row for each metric)
        rows.push(
          <TableRow
            key={`${portfolio}-${metric.key}-target`}
            sx={{
              backgroundColor: '#f0f4f8',
              '&:hover': { backgroundColor: '#e8f0fa' },
            }}
          >
            {/* Entity cell — spans all metric rows (target + actual) */}
            {isFirstMetric && (
              <TableCell
                align="center"
                rowSpan={METRICS.length * 2}
                sx={{
                  position: 'sticky',
                  left: 0,
                  backgroundColor: '#e2f3f9',
                  zIndex: 10,
                  borderRight: BORDER_MEDIUM,
                  borderBottom: BORDER_DARK,
                  verticalAlign: 'middle',
                  width: COL_ENTITY,
                  minWidth: COL_ENTITY,
                  maxWidth: COL_ENTITY,
                  cursor: platforms.length > 0 ? 'pointer' : 'default',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
                onClick={() => platforms.length > 0 && togglePortfolio(portfolio)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  {platforms.length > 0
                    ? isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />
                    : <Box sx={{ width: 16 }} />}
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                    {portfolio}
                  </Typography>
                </Box>
              </TableCell>
            )}

            {/* Metric label - spans 2 rows (target + actual) */}
            <TableCell
              align="center"
              rowSpan={2}
              sx={{
                position: 'sticky',
                left: COL_ENTITY,
                backgroundColor: '#e2f3f9',
                zIndex: 9,
                width: COL_METRIC,
                minWidth: COL_METRIC,
                maxWidth: COL_METRIC,
                borderRight: BORDER_DARK,
                borderBottom: BORDER_DARK,
                whiteSpace: 'nowrap',
                py: 0.8,
                verticalAlign: 'middle',
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#000000' }}>
                {metric.label}
              </Typography>
            </TableCell>

            {/* Target label */}
            <TableCell
              align="center"
              sx={{
                position: 'sticky',
                left: COL_ENTITY + COL_METRIC,
                backgroundColor: '#e2f3f9',
                zIndex: 8,
                borderRight: BORDER_DARK,
                boxShadow: FROZEN_COL_SHADOW,
                fontSize: '0.72rem',
                fontWeight: 700,
                color: '#000000',
                whiteSpace: 'nowrap',
                py: 0.6,
                width: COL_TYPE,
                minWidth: COL_TYPE,
                maxWidth: COL_TYPE,
              }}
            >
              Target
            </TableCell>

            {/* Target period cells - neutral color */}
            {periods.map(period => {
              const targetDisplay = metric.key === 'red_channel_count' ? '0' : `${metric.target}%`;
              return (
                <TableCell
                  key={period}
                  align="center"
                  sx={{
                    backgroundColor: 'white',
                    color: '#424242',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    borderRight: BORDER_MEDIUM,
                    borderBottom: BORDER_LIGHT,
                    minWidth: 90,
                    py: 0.6,
                  }}
                >
                  {targetDisplay}
                </TableCell>
              );
            })}
          </TableRow>
        );

        // Actual row (second row for each metric)
        rows.push(
          <TableRow
            key={`${portfolio}-${metric.key}-actual`}
            sx={{
              backgroundColor: '#f0f4f8',
              '&:hover': { backgroundColor: '#e8f0fa' },
            }}
          >
            {/* Actual label */}
            <TableCell
              align="center"
              sx={{
                position: 'sticky',
                left: COL_ENTITY + COL_METRIC,
                backgroundColor: '#e2f3f9',
                zIndex: 8,
                borderRight: BORDER_DARK,
                boxShadow: FROZEN_COL_SHADOW,
                borderBottom: BORDER_DARK,
                fontSize: '0.72rem',
                fontWeight: 700,
                color: '#000000',
                whiteSpace: 'nowrap',
                py: 0.6,
                width: COL_TYPE,
                minWidth: COL_TYPE,
                maxWidth: COL_TYPE,
              }}
            >
              Actual
            </TableCell>

            {/* Actual period cells - red/green coloring */}
            {periods.map((period, pIdx) => {
              const metricData = getPortfolioMetric(portfolio, period, metric.key);
              const val = metricData.value;
              const bg = getCellBg(metric, val);
              const prevPeriod = pIdx < periods.length - 1 ? periods[pIdx + 1] : null;
              const prevMetricData = prevPeriod ? getPortfolioMetric(portfolio, prevPeriod, metric.key) : null;
              const prevVal = prevMetricData?.value ?? null;
              return (
                <TableCell
                  key={period}
                  align="center"
                  sx={{
                    backgroundColor: bg,
                    color: bg === '#7FD175' ? 'black' : (bg !== 'transparent' ? 'white' : 'inherit'),
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderRight: BORDER_MEDIUM,
                    borderBottom: BORDER_DARK,
                    minWidth: 90,
                    py: 0.6,
                  }}
                >
                  {metric.formatValue(val, metricData.count, metricData.total)}
                  {renderTrendArrow(val, prevVal, metric.higherIsBetter)}
                </TableCell>
              );
            })}
          </TableRow>
        );
      });

      // Platform rows when expanded
      if (isExpanded) {
        platforms.forEach(platform => {
          METRICS.forEach((metric, mIdx) => {
            const isFirstMetric = mIdx === 0;
            const isLastMetric = mIdx === METRICS.length - 1;

            // Target row for platform
            rows.push(
              <TableRow
                key={`${portfolio}|${platform}-${metric.key}-target`}
                sx={{
                  backgroundColor: '#fafafa',
                }}
              >
                {isFirstMetric && (
                  <TableCell
                    align="center"
                    rowSpan={METRICS.length * 2}
                    sx={{
                      position: 'sticky',
                      left: 0,
                      backgroundColor: '#fafafa',
                      zIndex: 10,
                      pl: 4,
                      borderRight: BORDER_MEDIUM,
                      borderBottom: BORDER_LIGHT,
                      verticalAlign: 'middle',
                      width: COL_ENTITY,
                      minWidth: COL_ENTITY,
                      maxWidth: COL_ENTITY,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 16 }} />
                      <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 400 }}>
                        {platform}
                      </Typography>
                    </Box>
                  </TableCell>
                )}

{/* Metric label - spans 2 rows (target + actual) */}
            <TableCell
              align="center"
              rowSpan={2}
              sx={{
                position: 'sticky',
                left: COL_ENTITY,
                backgroundColor: '#fafafa',
                zIndex: 9,
                width: COL_METRIC,
                minWidth: COL_METRIC,
                maxWidth: COL_METRIC,
                borderRight: BORDER_DARK,
                borderBottom: BORDER_DARK,
                whiteSpace: 'nowrap',
                py: 0.8,
                verticalAlign: 'middle',
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#000000' }}>
                {metric.label}
              </Typography>
            </TableCell>

            {/* Target label */}
            <TableCell
              align="center"
              sx={{
                position: 'sticky',
                left: COL_ENTITY + COL_METRIC,
                backgroundColor: '#fafafa',
                zIndex: 8,
                borderRight: BORDER_DARK,
                boxShadow: FROZEN_COL_SHADOW,
                fontSize: '0.72rem',
                fontWeight: 700,
                color: '#000000',
                whiteSpace: 'nowrap',
                py: 0.6,
                width: COL_TYPE,
                minWidth: COL_TYPE,
                maxWidth: COL_TYPE,
              }}
            >
              Target
            </TableCell>

            {/* Target period cells - neutral color */}
            {periods.map(period => {
              const targetDisplay = metric.key === 'red_channel_count' ? '0' : `${metric.target}%`;
              return (
                <TableCell
                  key={period}
                  align="center"
                  sx={{
                    backgroundColor: 'white',
                    color: '#424242',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    borderRight: BORDER_MEDIUM,
                    borderBottom: BORDER_LIGHT,
                    minWidth: 90,
                    py: 0.6,
                  }}
                >
                  {targetDisplay}
                </TableCell>
              );
            })}
          </TableRow>
        );

            // Actual row for platform
            rows.push(
              <TableRow
                key={`${portfolio}|${platform}-${metric.key}-actual`}
                sx={{
                  backgroundColor: '#fafafa',
                  borderBottom: isLastMetric ? BORDER_LIGHT : undefined,
                }}
              >
                {/* Actual label */}
                <TableCell
                  align="center"
                  sx={{
                    position: 'sticky',
                    left: COL_ENTITY + COL_METRIC,
                    backgroundColor: '#fafafa',
                    zIndex: 8,
                    borderRight: BORDER_DARK,
                    boxShadow: FROZEN_COL_SHADOW,
                    borderBottom: BORDER_DARK,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: '#000000',
                    whiteSpace: 'nowrap',
                    py: 0.6,
                    width: COL_TYPE,
                    minWidth: COL_TYPE,
                    maxWidth: COL_TYPE,
                  }}
                >
                  Actual
                </TableCell>

                {/* Actual period cells - red/green coloring */}
                {periods.map((period, pIdx) => {
                  const rec = lookup.get(`${portfolio}|${platform}|${period}`);
                  const metricKey = metric.key as AssetChangeMetricKey;
                  const val = rec ? (rec[metricKey] as number | null) : null;

                  // Previous period for trend arrow (now from right since reversed)
                  const prevPeriod = pIdx < periods.length - 1 ? periods[pIdx + 1] : null;
                  const prevRec = prevPeriod ? lookup.get(`${portfolio}|${platform}|${prevPeriod}`) : null;
                  const prevVal = prevRec ? (prevRec[metricKey] as number | null) : null;

                  // Get count and total for metrics that need them
                  let count: number | null = null;
                  let total: number | null = null;
                  if (rec) {
                    if (metricKey === 'change_visibility_pct') {
                      count = rec.onboarded_mvc_count;
                      total = rec.total_mvc_count;
                    }
                    /* restore/failover tab removed:
                    } else if (metricKey === 'restore_test_pct_last_12_months') {
                      count = rec.restore_test_count_last_12_months;
                      total = rec.total_mvc_count;
                    } else if (metricKey === 'failover_test_pct_last_12_months') {
                      count = rec.failover_test_count_last_12_months;
                      total = rec.total_mvc_count;
                    }
                    */
                  }

                  const bg = getCellBg(metric, val);
                  return (
                    <TableCell
                      key={period}
                      align="center"
                      sx={{
                        backgroundColor: bg,
                        color: bg === '#7FD175' ? 'black' : (bg !== 'transparent' ? 'white' : 'inherit'),
                        fontWeight: 500,
                        fontSize: '0.78rem',
                        borderRight: BORDER_MEDIUM,
                        borderBottom: BORDER_DARK,
                        minWidth: 90,
                        py: 0.6,
                      }}
                    >
                      {metric.formatValue(val, count, total)}
                      {renderTrendArrow(val, prevVal, metric.higherIsBetter)}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          });
        });
      }
    });

    return rows;
  };

  // ─── Calculate P1/P2 totals across all periods ───────────────────────────
  const p1p2Totals = useMemo(() => {
    const totals = new Map<string, { count: number; total: number; percentage: number }>();
    
    // Sum up all counts and totals across all periods
    let totalP1P2All = 0;
    let totalCausedByChange = 0;
    let totalDetectedByAlerts = 0;
    
    filteredP1P2.forEach(rec => {
      totalP1P2All += rec.total_p1p2;
      totalCausedByChange += rec.p1_p2_caused_by_change_count;
      totalDetectedByAlerts += rec.p1_p2_detected_by_alerts_count;
    });
    
    // Calculate percentages
    const causedByChangePercentage = totalP1P2All > 0 
      ? (totalCausedByChange / totalP1P2All) * 100 
      : 0;
    const detectedByAlertsPercentage = totalP1P2All > 0 
      ? (totalDetectedByAlerts / totalP1P2All) * 100 
      : 0;
    
    totals.set('p1_p2_caused_by_change_percentage', {
      count: totalCausedByChange,
      total: totalP1P2All,
      percentage: causedByChangePercentage,
    });
    
    totals.set('p1_p2_detected_by_alerts_percentage', {
      count: totalDetectedByAlerts,
      total: totalP1P2All,
      percentage: detectedByAlertsPercentage,
    });
    
    return totals;
  }, [filteredP1P2]);

  // ─── Render P1/P2 rows ────────────────────────────────────────────────────
  const renderP1P2Rows = (): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];

    METRICS.forEach((metric, mIdx) => {
      const isFirstMetric = mIdx === 0;
      const isLastMetric = mIdx === METRICS.length - 1;

      // Target row
      rows.push(
        <TableRow
          key={`${metric.key}-target`}
          sx={{
            backgroundColor: '#f0f4f8',
            '&:hover': { backgroundColor: '#e8f0fa' },
          }}
        >
          {/* Metric label - spans 2 rows (target + actual) */}
          <TableCell
            align="center"
            rowSpan={2}
            sx={{
              position: 'sticky',
              left: 0,
              backgroundColor: '#e2f3f9',
              zIndex: 10,
              width: 240,
              minWidth: 240,
              maxWidth: 240,
              borderRight: BORDER_DARK,
              borderBottom: BORDER_DARK,
              whiteSpace: 'nowrap',
              py: 0.8,
              verticalAlign: 'middle',
            }}
          >
            <Typography variant="caption" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#000000' }}>
              {metric.label}
            </Typography>
          </TableCell>

          {/* Target label */}
          <TableCell
            align="center"
            sx={{
              position: 'sticky',
              left: 240,
              backgroundColor: '#e2f3f9',
              zIndex: 9,
              borderRight: BORDER_DARK,
              boxShadow: FROZEN_COL_SHADOW,
              fontSize: '0.72rem',
              fontWeight: 700,
              color: '#000000',
              whiteSpace: 'nowrap',
              py: 0.6,
              width: COL_TYPE,
              minWidth: COL_TYPE,
              maxWidth: COL_TYPE,
            }}
          >
            Target
          </TableCell>

          {/* Target period cells - neutral color */}
          {p1p2Periods.map(period => {
            const targetDisplay = metric.higherIsBetter ? `≥${metric.target}%` : `≤${metric.target}%`;
            return (
              <TableCell
                key={period}
                align="center"
                sx={{
                  backgroundColor: 'white',
                  color: '#424242',
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  borderRight: BORDER_MEDIUM,
                  borderBottom: BORDER_LIGHT,
                  minWidth: 90,
                  py: 0.6,
                }}
              >
                {targetDisplay}
              </TableCell>
            );
          })}
          {/* Total column - target */}
          <TableCell
            align="center"
            sx={{
              backgroundColor: 'white',
              color: '#424242',
              fontWeight: 600,
              fontSize: '0.78rem',
              borderBottom: BORDER_LIGHT,
              minWidth: 90,
              py: 0.6,
            }}
          >
            {metric.higherIsBetter ? `≥${metric.target}%` : `≤${metric.target}%`}
          </TableCell>
        </TableRow>
      );

      // Actual row
      rows.push(
        <TableRow
          key={`${metric.key}-actual`}
          sx={{
            backgroundColor: '#f0f4f8',
            '&:hover': { backgroundColor: '#e8f0fa' },
          }}
        >
          {/* Actual label */}
          <TableCell
            align="center"
            sx={{
              position: 'sticky',
              left: 240,
              backgroundColor: '#e2f3f9',
              zIndex: 9,
              borderRight: BORDER_DARK,
              boxShadow: FROZEN_COL_SHADOW,
              borderBottom: BORDER_DARK,
              fontSize: '0.72rem',
              fontWeight: 700,
              color: '#000000',
              whiteSpace: 'nowrap',
              py: 0.6,
              width: COL_TYPE,
              minWidth: COL_TYPE,
              maxWidth: COL_TYPE,
            }}
          >
            Actual
          </TableCell>

          {/* Actual period cells - red/green coloring */}
          {p1p2Periods.map((period, pIdx) => {
            const rec = p1p2Lookup.get(period);
            const val = rec ? rec[metric.key as keyof P1P2Record] as number : null;

            // Previous period for trend arrow (now from right since reversed)
            const prevPeriod = pIdx < p1p2Periods.length - 1 ? p1p2Periods[pIdx + 1] : null;
            const prevRec = prevPeriod ? p1p2Lookup.get(prevPeriod) : null;
            const prevVal = prevRec ? prevRec[metric.key as keyof P1P2Record] as number : null;

            // Get count and total for formatting
            let count: number | null = null;
            let total: number | null = null;
            if (rec) {
              total = rec.total_p1p2;
              if (metric.key === 'p1_p2_caused_by_change_percentage') {
                count = rec.p1_p2_caused_by_change_count;
              } else if (metric.key === 'p1_p2_detected_by_alerts_percentage') {
                count = rec.p1_p2_detected_by_alerts_count;
              }
            }

            const bg = getCellBg(metric, val);
            return (
              <TableCell
                key={period}
                align="center"
                sx={{
                  backgroundColor: bg,
                  color: bg === '#7FD175' ? 'black' : (bg !== 'transparent' ? 'white' : 'inherit'),
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  borderRight: BORDER_MEDIUM,
                  borderBottom: BORDER_DARK,
                  minWidth: 90,
                  py: 0.6,
                }}
              >
                {metric.formatValue(val, count, total)}
                {renderTrendArrow(val, prevVal, metric.higherIsBetter)}
              </TableCell>
            );
          })}
          {/* Total column - actual */}
          {(() => {
            const totalData = p1p2Totals.get(metric.key);
            const totalVal = totalData ? totalData.percentage : null;
            const totalCount = totalData ? totalData.count : null;
            const totalTotal = totalData ? totalData.total : null;
            const bg = getCellBg(metric, totalVal);
            
            return (
              <TableCell
                align="center"
                sx={{
                  backgroundColor: bg,
                  color: bg === '#7FD175' ? 'black' : (bg !== 'transparent' ? 'white' : 'inherit'),
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  borderBottom: BORDER_DARK,
                  minWidth: 90,
                  py: 0.6,
                }}
              >
                {metric.formatValue(totalVal, totalCount, totalTotal)}
              </TableCell>
            );
          })()}
        </TableRow>
      );
    });

    return rows;
  };

  // ─── Render platform-only rows (no portfolio grouping) ───────────────────
  const renderPlatformOnlyRows = (): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];

    allUniquePlatforms.forEach(platform => {
      // We need to aggregate data across all portfolios for this platform
      METRICS.forEach((metric, mIdx) => {
        const isFirstMetric = mIdx === 0;
        const isLastMetric = mIdx === METRICS.length - 1;

        // Target row
        rows.push(
          <TableRow
            key={`${platform}-${metric.key}-target`}
            sx={{
              backgroundColor: '#f0f4f8',
              '&:hover': { backgroundColor: '#e8f0fa' },
            }}
          >
            {/* Platform cell — spans all metric rows (target + actual) */}
            {isFirstMetric && (
              <TableCell
                align="center"
                rowSpan={METRICS.length * 2}
                sx={{
                  position: 'sticky',
                  left: 0,
                  backgroundColor: '#e2f3f9',
                  zIndex: 10,
                  borderRight: BORDER_MEDIUM,
                  borderBottom: BORDER_DARK,
                  verticalAlign: 'middle',
                  width: COL_ENTITY,
                  minWidth: COL_ENTITY,
                  maxWidth: COL_ENTITY,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                    {platform}
                  </Typography>
                </Box>
              </TableCell>
            )}

            {/* Metric label - spans 2 rows (target + actual) */}
            <TableCell
              align="center"
              rowSpan={2}
              sx={{
                position: 'sticky',
                left: COL_ENTITY,
                backgroundColor: '#e2f3f9',
                zIndex: 9,
                width: COL_METRIC,
                minWidth: COL_METRIC,
                maxWidth: COL_METRIC,
                borderRight: BORDER_DARK,
                borderBottom: BORDER_DARK,
                whiteSpace: 'nowrap',
                py: 0.8,
                verticalAlign: 'middle',
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#000000' }}>
                {metric.label}
              </Typography>
            </TableCell>

            {/* Target label */}
            <TableCell
              align="center"
              sx={{
                position: 'sticky',
                left: COL_ENTITY + COL_METRIC,
                backgroundColor: '#e2f3f9',
                zIndex: 8,
                borderRight: BORDER_DARK,
                boxShadow: FROZEN_COL_SHADOW,
                fontSize: '0.72rem',
                fontWeight: 700,
                color: '#000000',
                whiteSpace: 'nowrap',
                py: 0.6,
                width: COL_TYPE,
                minWidth: COL_TYPE,
                maxWidth: COL_TYPE,
              }}
            >
              Target
            </TableCell>

            {/* Target period cells - neutral color */}
            {periods.map(period => {
              const targetDisplay = metric.key === 'red_channel_count' ? '0' : `${metric.target}%`;
              return (
                <TableCell
                  key={period}
                  align="center"
                  sx={{
                    backgroundColor: 'white',
                    color: '#424242',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    borderRight: BORDER_MEDIUM,
                    borderBottom: BORDER_LIGHT,
                    minWidth: 90,
                    py: 0.6,
                  }}
                >
                  {targetDisplay}
                </TableCell>
              );
            })}
          </TableRow>
        );

        // Actual row
        rows.push(
          <TableRow
            key={`${platform}-${metric.key}-actual`}
            sx={{
              backgroundColor: '#f0f4f8',
              '&:hover': { backgroundColor: '#e8f0fa' },
            }}
          >
            {/* Actual label */}
            <TableCell
              align="center"
              sx={{
                position: 'sticky',
                left: COL_ENTITY + COL_METRIC,
                backgroundColor: '#e2f3f9',
                zIndex: 8,
                borderRight: BORDER_DARK,
                boxShadow: FROZEN_COL_SHADOW,
                borderBottom: BORDER_DARK,
                fontSize: '0.72rem',
                fontWeight: 700,
                color: '#000000',
                whiteSpace: 'nowrap',
                py: 0.6,
                width: COL_TYPE,
                minWidth: COL_TYPE,
                maxWidth: COL_TYPE,
              }}
            >
              Actual
            </TableCell>

            {/* Actual period cells - red/green coloring */}
            {periods.map((period, pIdx) => {
              // Find all records for this platform in this period (across all portfolios)
              const records = filtered.filter(
                d => d.platform === platform && d.period_value === period
              );

              // For aggregation: we'll take the first record (they should be consistent within a platform)
              const rec = records.length > 0 ? records[0] : null;
              const metricKey = metric.key as AssetChangeMetricKey;
              const val = rec ? (rec[metricKey] as number | null) : null;

              // Previous period for trend arrow (now from right since reversed)
              const prevPeriod = pIdx < periods.length - 1 ? periods[pIdx + 1] : null;
              const prevRecords = prevPeriod ? filtered.filter(
                d => d.platform === platform && d.period_value === prevPeriod
              ) : [];
              const prevRec = prevRecords.length > 0 ? prevRecords[0] : null;
              const prevVal = prevRec ? (prevRec[metricKey] as number | null) : null;

              // Get count and total for metrics that need them
              let count: number | null = null;
              let total: number | null = null;
              if (rec) {
                if (metricKey === 'change_visibility_pct') {
                  count = rec.onboarded_mvc_count;
                  total = rec.total_mvc_count;
                }
              }

              const bg = getCellBg(metric, val);
              return (
                <TableCell
                  key={period}
                  align="center"
                  sx={{
                    backgroundColor: bg,
                    color: bg === '#7FD175' ? 'black' : (bg !== 'transparent' ? 'white' : 'inherit'),
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderRight: BORDER_MEDIUM,
                    borderBottom: BORDER_DARK,
                    minWidth: 90,
                    py: 0.6,
                  }}
                >
                  {metric.formatValue(val, count, total)}
                  {renderTrendArrow(val, prevVal, metric.higherIsBetter)}
                </TableCell>
              );
            })}
          </TableRow>
        );
      });
    });

    return rows;
  };

  // ─── Export to Excel with colors ──────────────────────────────────────────
  const exportToExcel = () => {
    const exportData: any[] = [];
    
    if (selectedTab === 'p1-p2') {
      // Export P1/P2 data
      METRICS.forEach(metric => {
        const targetRow: any = { 'Metric': metric.label, 'Type': 'Target' };
        const actualRow: any = { 'Metric': metric.label, 'Type': 'Actual' };
        
        p1p2Periods.forEach(period => {
          const periodLabel = formatPeriodLabel(period, periodType);
          const rec = p1p2Lookup.get(period);
          
          // Target
          const targetDisplay = metric.higherIsBetter ? `≥${metric.target}%` : `≤${metric.target}%`;
          targetRow[periodLabel] = targetDisplay;
          
          // Actual
          const val = rec ? rec[metric.key as keyof P1P2Record] as number : null;
          let count: number | null = null;
          let total: number | null = null;
          if (rec) {
            total = rec.total_p1p2;
            if (metric.key === 'p1_p2_caused_by_change_percentage') {
              count = rec.p1_p2_caused_by_change_count;
            } else if (metric.key === 'p1_p2_detected_by_alerts_percentage') {
              count = rec.p1_p2_detected_by_alerts_count;
            }
          }
          actualRow[periodLabel] = metric.formatValue(val, count, total);
        });
        
        // Add Total column
        const targetDisplay = metric.higherIsBetter ? `≥${metric.target}%` : `≤${metric.target}%`;
        targetRow['Total'] = targetDisplay;
        
        const totalData = p1p2Totals.get(metric.key);
        const totalVal = totalData ? totalData.percentage : null;
        const totalCount = totalData ? totalData.count : null;
        const totalTotal = totalData ? totalData.total : null;
        actualRow['Total'] = metric.formatValue(totalVal, totalCount, totalTotal);
        
        exportData.push(targetRow);
        exportData.push(actualRow);
      });
    } else if (viewBy === 'platform') {
      // Export platform-only data
      allUniquePlatforms.forEach(platform => {
        METRICS.forEach(metric => {
          const targetRow: any = { 'Platform': platform, 'Metric': metric.label, 'Type': 'Target' };
          const actualRow: any = { 'Platform': platform, 'Metric': metric.label, 'Type': 'Actual' };
          
          periods.forEach(period => {
            const periodLabel = formatPeriodLabel(period, periodType);
            
            // Target
            const targetDisplay = metric.key === 'red_channel_count' ? '0' : `${metric.target}%`;
            targetRow[periodLabel] = targetDisplay;
            
            // Actual
            const records = filtered.filter(d => d.platform === platform && d.period_value === period);
            const rec = records.length > 0 ? records[0] : null;
            const metricKey = metric.key as AssetChangeMetricKey;
            const val = rec ? (rec[metricKey] as number | null) : null;
            
            let count: number | null = null;
            let total: number | null = null;
            if (rec && metricKey === 'change_visibility_pct') {
              count = rec.onboarded_mvc_count;
              total = rec.total_mvc_count;
            }
            
            actualRow[periodLabel] = metric.formatValue(val, count, total);
          });
          
          exportData.push(targetRow);
          exportData.push(actualRow);
        });
      });
    } else {
      // Export portfolio data with drill-down
      portfolios.forEach(({ portfolio, platforms: platformList }) => {
        // Portfolio level
        METRICS.forEach(metric => {
          const targetRow: any = { 'Portfolio/Platform': portfolio, 'Metric': metric.label, 'Type': 'Target' };
          const actualRow: any = { 'Portfolio/Platform': portfolio, 'Metric': metric.label, 'Type': 'Actual' };
          
          periods.forEach(period => {
            const periodLabel = formatPeriodLabel(period, periodType);
            
            // Target
            const targetDisplay = metric.key === 'red_channel_count' ? '0' : `${metric.target}%`;
            targetRow[periodLabel] = targetDisplay;
            
            // Actual
            const metricData = getPortfolioMetric(portfolio, period, metric.key);
            actualRow[periodLabel] = metric.formatValue(metricData.value, metricData.count, metricData.total);
          });
          
          exportData.push(targetRow);
          exportData.push(actualRow);
        });
        
        // Platform level - only include if portfolio is expanded
        if (expandedPortfolios.has(portfolio)) {
          platformList.forEach(platform => {
            METRICS.forEach(metric => {
              const targetRow: any = { 'Portfolio/Platform': `  ${platform}`, 'Metric': metric.label, 'Type': 'Target' };
              const actualRow: any = { 'Portfolio/Platform': `  ${platform}`, 'Metric': metric.label, 'Type': 'Actual' };
              
              periods.forEach(period => {
                const periodLabel = formatPeriodLabel(period, periodType);
                
                // Target
                const targetDisplay = metric.key === 'red_channel_count' ? '0' : `${metric.target}%`;
                targetRow[periodLabel] = targetDisplay;
                
                // Actual
                const rec = lookup.get(`${portfolio}|${platform}|${period}`);
                const metricKey = metric.key as AssetChangeMetricKey;
                const val = rec ? (rec[metricKey] as number | null) : null;
                
                let count: number | null = null;
                let total: number | null = null;
                if (rec && metricKey === 'change_visibility_pct') {
                  count = rec.onboarded_mvc_count;
                  total = rec.total_mvc_count;
                }
                
                actualRow[periodLabel] = metric.formatValue(val, count, total);
              });
              
              exportData.push(targetRow);
              exportData.push(actualRow);
            });
          });
        }
      });
    }
    
    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Apply colors to cells
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    
    // Excel color functions - user specified colors
    const getExcelBgColor = (metric: MetricConfig, value: number | null): string => {
      if (value === null) return 'FFFFFFFF'; // White for N/A
      if (metric.isGreen(value)) return 'FF7FD175'; // On target green: #7FD175
      if (metric.isRed(value)) return 'FFB80012'; // Below target red: #B80012
      return 'FFFFFFFF'; // White for amber/other
    };
    
    const getExcelFontColor = (metric: MetricConfig, value: number | null): string => {
      if (value === null) return 'FF000000'; // Black for N/A
      if (metric.isGreen(value)) return 'FF000000'; // Black text on green
      if (metric.isRed(value)) return 'FFFFFFFF'; // White text on red
      return 'FF000000'; // Black for other
    };
    
    // Initialize merges array
    const merges: any[] = [];
    
    // Track merge groups for portfolio/platform and metrics
    const entityMerges = new Map<string, { start: number; count: number }>();
    const metricMerges: Array<{ row: number }> = [];
    
    // Build merge map
    exportData.forEach((row, idx) => {
      const entity = selectedTab === 'p1-p2' ? null : (row['Portfolio/Platform'] || row['Platform']);
      
      if (entity && selectedTab !== 'p1-p2') {
        // Track portfolio/platform merges
        if (!entityMerges.has(entity)) {
          entityMerges.set(entity, { start: idx + 1, count: 0 }); // +1 for header row
        }
        entityMerges.get(entity)!.count++;
      }
      
      // Track metric merges (every target row starts a 2-row merge for the metric)
      if (row['Type'] === 'Target') {
        metricMerges.push({ row: idx + 1 }); // +1 for header row
      }
    });
    
    // Add portfolio/platform merges (column A)
    if (selectedTab !== 'p1-p2') {
      entityMerges.forEach((info) => {
        if (info.count > 1) {
          merges.push({
            s: { r: info.start, c: 0 },
            e: { r: info.start + info.count - 1, c: 0 }
          });
        }
      });
    }
    
    // Add metric merges (column B for P1/P2, column A for P1/P2 view or column B for others)
    const metricCol = selectedTab === 'p1-p2' ? 0 : 1;
    metricMerges.forEach((info) => {
      merges.push({
        s: { r: info.row, c: metricCol },
        e: { r: info.row + 1, c: metricCol } // Merge target + actual rows
      });
    });
    
    // Apply merges to worksheet
    ws['!merges'] = merges;
    
    // Apply styling to all cells
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[cellAddress];
        if (!cell) continue;
        
        // Initialize cell style
        if (!cell.s) cell.s = {};
        
        // Header row styling
        if (R === 0) {
          cell.s = {
            fill: { fgColor: { rgb: 'FF42B0D5' } }, // User specified: #42B0D5
            font: { color: { rgb: 'FFFFFFFF' }, bold: true },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
              top: { style: 'thin', color: { rgb: 'FF000000' } },
              bottom: { style: 'thin', color: { rgb: 'FF000000' } },
              left: { style: 'thin', color: { rgb: 'FF000000' } },
              right: { style: 'thin', color: { rgb: 'FF000000' } }
            }
          };
          continue;
        }
        
        const row = exportData[R - 1];
        if (!row || row['Type'] !== 'Actual') {
          // Style for target rows and label columns
          const isLabelColumn = C < (selectedTab === 'p1-p2' ? 2 : 3);
          cell.s = {
            fill: { fgColor: { rgb: isLabelColumn ? 'FFE2F3F9' : 'FFFFFFFF' } }, // Labels: #e2f3f9, Target values: white
            font: { color: { rgb: 'FF000000' }, bold: true },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
              top: { style: 'thin', color: { rgb: 'FF000000' } },
              bottom: { style: 'thin', color: { rgb: 'FF000000' } },
              left: { style: 'thin', color: { rgb: 'FF000000' } },
              right: { style: 'thin', color: { rgb: 'FF000000' } }
            }
          };
          continue;
        }
        
        // Actual row - apply color coding
        const metricLabel = row['Metric'];
        const metric = METRICS.find(m => m.label === metricLabel);
        
        // Label columns for actual rows
        if (C < (selectedTab === 'p1-p2' ? 2 : 3)) {
          cell.s = {
            fill: { fgColor: { rgb: 'FFE2F3F9' } }, // Label color
            font: { bold: true, color: { rgb: 'FF000000' } },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
              top: { style: 'thin', color: { rgb: 'FF000000' } },
              bottom: { style: 'thin', color: { rgb: 'FF000000' } },
              left: { style: 'thin', color: { rgb: 'FF000000' } },
              right: { style: 'thin', color: { rgb: 'FF000000' } }
            }
          };
          continue;
        }
        
        if (!metric) continue;
        
        // Extract numeric value from cell
        const cellValue = cell.v;
        let numericValue: number | null = null;
        
        if (typeof cellValue === 'string') {
          // Try to extract percentage or number
          const match = cellValue.match(/^([\d.]+)%/);
          if (match) {
            numericValue = parseFloat(match[1]);
          } else if (cellValue.match(/^\d+$/)) {
            numericValue = parseInt(cellValue);
          }
        } else if (typeof cellValue === 'number') {
          numericValue = cellValue;
        }
        
        if (numericValue !== null) {
          const bgColor = getExcelBgColor(metric, numericValue);
          const fontColor = getExcelFontColor(metric, numericValue);
          
          cell.s = {
            fill: { fgColor: { rgb: bgColor } },
            font: { color: { rgb: fontColor }, bold: true },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
              top: { style: 'thin', color: { rgb: 'FF000000' } },
              bottom: { style: 'thin', color: { rgb: 'FF000000' } },
              left: { style: 'thin', color: { rgb: 'FF000000' } },
              right: { style: 'thin', color: { rgb: 'FF000000' } }
            }
          };
        } else {
          // N/A or empty cells
          cell.s = {
            fill: { fgColor: { rgb: 'FFFFFFFF' } },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
              top: { style: 'thin', color: { rgb: 'FF000000' } },
              bottom: { style: 'thin', color: { rgb: 'FF000000' } },
              left: { style: 'thin', color: { rgb: 'FF000000' } },
              right: { style: 'thin', color: { rgb: 'FF000000' } }
            }
          };
        }
      }
    }
    
    // Set column widths
    const colWidths = [];
    for (let C = range.s.c; C <= range.e.c; C++) {
      if (C === 0) {
        colWidths.push({ wch: 30 }); // Portfolio/Platform or Metric column
      } else if (C === 1) {
        colWidths.push({ wch: 30 }); // Metric or Type column
      } else if (C === 2 && selectedTab !== 'p1-p2') {
        colWidths.push({ wch: 10 }); // Type column
      } else {
        colWidths.push({ wch: 15 }); // Period columns
      }
    }
    ws['!cols'] = colWidths;
    
    // Create workbook and export
    const wb = XLSX.utils.book_new();
    const sheetName = selectedTab === 'p1-p2' ? 'P1P2 Metrics' : 
                      selectedTab === 'certification' ? 'Asset and Change Metrics' : 'Metrics';
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    
    // Generate filename
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const viewByStr = selectedTab !== 'p1-p2' ? `_${viewBy}` : '';
    const filename = `Asset_Change_Metrics_${viewByStr}_${dateStr}.xlsx`;
    
    XLSX.writeFile(wb, filename);
  };

  const hasFilters = selectedPortfolios.length > 0 || selectedPlatforms.length > 0 || selectedTimeRange !== 'ytd';

  return (
    <Box >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#001a35' }}>
            Asset & Change Metrics
          </Typography>
        
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            startIcon={<FileDownload />} 
            variant="contained" 
            size="small" 
            onClick={exportToExcel}
            disabled={loading || (selectedTab === 'p1-p2' ? p1p2Periods.length === 0 : (viewBy === 'platform' ? allUniquePlatforms.length === 0 : portfolios.length === 0))}
            sx={{ backgroundColor: '#00843d', '&:hover': { backgroundColor: '#006d32' } }}
          >
            Export to Excel
          </Button>
          <Button startIcon={<Refresh />} variant="outlined" size="small" onClick={fetchData} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => {
            // Synchronously update metrics to avoid flicker
            const newTabMetrics = newValue === 'certification' ? CERTIFICATION_METRICS : P1P2_METRICS;
            setSelectedMetricKeys(newTabMetrics.map(m => m.key));
            setSelectedTab(newValue);
          }}
          sx={{
            
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
            },
          }}
        >
          <Tab label="Certification and Change Visibility" value="certification" />
          {/* <Tab label="Restore and Failover Test" value="restore-failover" /> */}{/* restore/failover tab removed */}
          <Tab label="P1/P2" value="p1-p2" />
        </Tabs>
      </Paper>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        {/* Row 1: period type + portfolio/platform filters + view by + clear */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
          {/* Period type toggle */}
          <ToggleButtonGroup
            value={periodType}
            exclusive
            onChange={(_, v) => v && setPeriodType(v)}
            size="small"
          >
            {(['WEEK', 'MONTH', 'QUARTER'] as const).map(pt => (
              <ToggleButton key={pt} value={pt} sx={{ px: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}>
                {PERIOD_TYPE_LABELS[pt]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {selectedTab !== 'p1-p2' && (
            <>
              {/* View By Filter */}
              <ToggleButtonGroup
                value={viewBy}
                exclusive
                onChange={(_, v) => v && startTransition(() => setViewBy(v))}
                size="small"
              >
                <ToggleButton value="portfolio" sx={{ px: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}>
                  Portfolio
                </ToggleButton>
                <ToggleButton value="platform" sx={{ px: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}>
                  Platform
                </ToggleButton>
              </ToggleButtonGroup>

              <Box sx={{ minWidth: 240 }}>
                {makeSelectAll('Portfolio', allPortfolios, selectedPortfolios, (v) => {
                  setSelectedPortfolios(v);
                  setSelectedPlatforms([]);
                })}
              </Box>

              <Box sx={{ minWidth: 240 }}>
                {makeSelectAll('Platform', allPlatforms, selectedPlatforms, setSelectedPlatforms)}
              </Box>
            </>
          )}

          {hasFilters && (
            <Button startIcon={<Clear />} size="small" variant="text" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </Box>

        {/* Row 1.5: Metric Selection Filter */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
          <Box sx={{ minWidth: 400, maxWidth: 600 }}>
            <Autocomplete
              multiple
              size="small"
              limitTags={2}
              options={ALL_TAB_METRICS.map(m => m.key)}
              value={selectedMetricKeys}
              onChange={(_, val) => {
                setSelectedMetricKeys(val);
              }}
              getOptionLabel={(option) => {
                const metric = ALL_TAB_METRICS.find(m => m.key === option);
                return metric ? metric.label : option;
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Select Metrics"
                  sx={{
                    '& .MuiInputBase-root': { fontSize: '0.8rem', minHeight: 36 },
                    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                  }}
                />
              )}
              renderOption={(props, option, { selected }) => {
                const metric = ALL_TAB_METRICS.find(m => m.key === option);
                return (
                  <li {...props}>
                    <Checkbox checked={selected} size="small" />
                    <ListItemText primary={metric?.label || option} sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' } }} />
                  </li>
                );
              }}
              renderTags={(vals, getTagProps) => {
                const isAll = vals.length === ALL_TAB_METRICS.length;
                if (isAll) return <Chip {...getTagProps({ index: 0 })} label="All Metrics" size="small" sx={{ fontSize: '0.7rem', height: 20 }} />;
                if (!vals.length) return null;
                return (
                  <>
                    <Chip
                      {...getTagProps({ index: 0 })}
                      label={ALL_TAB_METRICS.find(m => m.key === vals[0])?.label || vals[0]}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: 20, maxWidth: 120 }}
                    />
                    {vals.length > 1 && (
                      <Typography variant="caption" sx={{ ml: 0.5, fontSize: '0.7rem' }}>+{vals.length - 1}</Typography>
                    )}
                  </>
                );
              }}
              sx={{ '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' } }}
            />
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedMetricKeys(ALL_TAB_METRICS.map(m => m.key))}
            sx={{ fontSize: '0.75rem', textTransform: 'none' }}
          >
            Select All
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedMetricKeys([])}
            sx={{ fontSize: '0.75rem', textTransform: 'none' }}
          >
            Clear All
          </Button>
        </Box>

        {/* Row 2: quick time range presets */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5 }}>
            <DateRange sx={{ fontSize: 16, color: '#546e7a' }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#546e7a', whiteSpace: 'nowrap' }}>
              Time Range:
            </Typography>
          </Box>
          {QUICK_RANGES.map(({ value, label }) => (
            <Button
              key={value}
              size="small"
              variant={selectedTimeRange === value ? 'contained' : 'outlined'}
              onClick={() => setSelectedTimeRange(value)}
              sx={{
                px: 1.5,
                py: 0.4,
                fontSize: '0.75rem',
                fontWeight: 600,
                minWidth: 0,
                textTransform: 'none',
                borderRadius: 1,
                ...(selectedTimeRange === value
                  ? { backgroundColor: '#0067b3', color: 'white', '&:hover': { backgroundColor: '#001a35' } }
                  : { borderColor: '#cfd8dc', color: '#546e7a', '&:hover': { borderColor: '#0067b3', color: '#0067b3' } }),
              }}
            >
              {label}
            </Button>
          ))}
        </Box>

        {/* Custom picker */}
        <Collapse in={showCustomPicker}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1.5, flexWrap: 'wrap' }}>
            <Autocomplete
              multiple
              size="small"
              options={selectedTab === 'p1-p2' ? allAvailableP1P2Periods : allAvailablePeriods}
              value={customPeriods}
              onChange={(_, newValue) => {
                setCustomPeriods(newValue);
                if (newValue.length > 0) {
                  const sortedPeriods = [...newValue].sort();
                  setFilterStartPeriod(sortedPeriods[0]);
                  setFilterEndPeriod(sortedPeriods[sortedPeriods.length - 1]);
                } else {
                  setFilterStartPeriod('');
                  setFilterEndPeriod('');
                }
              }}
              getOptionLabel={(option) => formatPeriodLabel(option, periodType)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Select ${periodType === 'WEEK' ? 'Week(s)' : periodType === 'MONTH' ? 'Month(s)' : 'Quarter(s)'}`}
                  sx={{
                    '& .MuiInputBase-root': { fontSize: '0.8rem' },
                    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                  }}
                />
              )}
              renderTags={(vals, getTagProps) => (
                <>
                  {vals.slice(0, 2).map((val, idx) => (
                    <Chip
                      {...getTagProps({ index: idx })}
                      label={formatPeriodLabel(val, periodType)}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: 20, maxWidth: 100 }}
                    />
                  ))}
                  {vals.length > 2 && (
                    <Typography variant="caption" sx={{ ml: 0.5, fontSize: '0.7rem' }}>
                      +{vals.length - 2}
                    </Typography>
                  )}
                </>
              )}
              sx={{ minWidth: 280, maxWidth: 400 }}
            />
          </Box>
        </Collapse>
      </Paper>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mb: 1.5, alignItems: 'center' }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>Legend:</Typography>
        {[
          { color: '#7FD175', label: 'On Target' },
          { color: '#B80012', label: 'Below Target' },
          { color: 'transparent', label: 'N/A', border: '1px solid #000000' },
        ].map(({ color, label, border }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: 1, backgroundColor: color, border: border ?? 'none' }} />
            <Typography variant="caption">{label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Info message */}
      {selectedTab !== 'p1-p2' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, px: 1, py: 0.5, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
          <InfoOutlined sx={{ fontSize: 16, color: '#1976d2' }} />
          <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 500 }}>
            Limited Historical Data: Platform level data is not available before 2026-W14 and MVC Certification data is not available before 2026-W07.
          </Typography>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ overflow: 'hidden', position: 'relative' }}>
          {isPending && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.7)',
              zIndex: 20,
            }}>
              <CircularProgress />
            </Box>
          )}
          {/* Top scrollbar - only show when horizontal scrolling is needed */}
          {hasHorizontalScroll && (
            <Box 
              ref={topScrollRef}
              sx={{ 
                overflowX: 'auto', 
                overflowY: 'hidden',
                borderBottom: '1px solid #000000',
                backgroundColor: '#fafafa',
                height: '24px',
                width: containerWidth > 0 ? `${containerWidth}px` : '100%',
              }}
            >
              <Box sx={{ 
                height: '1px', 
                width: tableScrollWidth > 0 ? `${tableScrollWidth}px` : '100%',
              }} />
            </Box>
          )}
          
          <TableContainer
            ref={tableContainerRef}
            sx={{
              maxHeight: 'calc(100vh - 280px)',
              overflow: 'auto',
            }}
          >
          <Table size="small" stickyHeader sx={{ 
            borderCollapse: 'separate',
            borderSpacing: 0,
            '& .MuiTableCell-root': {
              borderTop: 0,
              borderLeft: 0,
              borderRight: '2px solid #000000',
              borderBottom: '0.25px solid #000000',
            },
            '& .MuiTableCell-head': {
              borderTop: 0,
              borderLeft: 0,
              borderBottom: '2px solid #000000',
              backgroundColor: '#42B0D5 !important',
              color: 'white !important',
            },
          }}>
            <TableHead>
              <TableRow>
                {selectedTab === 'p1-p2' ? (
                  <>
                    {/* Metric column for P1/P2 */}
                    <TableCell
                      align="center"
                      sx={{
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        zIndex: 13,
                        backgroundColor: '#42B0D5',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        borderRight: BORDER_DARK,
                        width: 240,
                        minWidth: 240,
                        maxWidth: 240,
                      }}
                    >
                      Metric
                    </TableCell>
                    {/* Row Type column (Target/Actual) */}
                    <TableCell
                      align="center"
                      sx={{
                        position: 'sticky',
                        top: 0,
                        left: 240,
                        zIndex: 12,
                        backgroundColor: '#42B0D5',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        borderRight: BORDER_DARK,
                        boxShadow: FROZEN_COL_SHADOW,
                        width: COL_TYPE,
                        minWidth: COL_TYPE,
                        maxWidth: COL_TYPE,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Type
                    </TableCell>
                    {/* Period columns */}
                    {p1p2Periods.map(period => (
                      <TableCell
                        key={period}
                        align="center"
                        sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 3,
                          backgroundColor: '#42B0D5',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.78rem',
                          borderRight: BORDER_MEDIUM,
                          minWidth: 90,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatPeriodLabel(period, periodType)}
                      </TableCell>
                    ))}
                    {/* Total column */}
                    <TableCell
                      align="center"
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 3,
                        backgroundColor: '#42B0D5',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        minWidth: 90,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Total
                    </TableCell>
                  </>
                ) : (
                  <>
                    {/* Portfolio/Platform column */}
                    <TableCell
                      align="center"
                      sx={{
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        zIndex: 13,
                        backgroundColor: '#42B0D5',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        borderRight: BORDER_MEDIUM,
                        width: COL_ENTITY,
                        minWidth: COL_ENTITY,
                        maxWidth: COL_ENTITY,
                      }}
                    >
                      {viewBy === 'platform' ? 'Platform' : 'Portfolio / Platform'}
                    </TableCell>
                    {/* Metric column */}
                    <TableCell
                      align="center"
                      sx={{
                        position: 'sticky',
                        top: 0,
                        left: COL_ENTITY,
                        zIndex: 12,
                        backgroundColor: '#42B0D5',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        borderRight: BORDER_DARK,
                        width: COL_METRIC,
                        minWidth: COL_METRIC,
                        maxWidth: COL_METRIC,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Metric
                    </TableCell>
                    {/* Row Type column (Target/Actual) */}
                    <TableCell
                      align="center"
                      sx={{
                        position: 'sticky',
                        top: 0,
                        left: COL_ENTITY + COL_METRIC,
                        zIndex: 11,
                        backgroundColor: '#42B0D5',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        borderRight: BORDER_DARK,
                        boxShadow: FROZEN_COL_SHADOW,
                        width: COL_TYPE,
                        minWidth: COL_TYPE,
                        maxWidth: COL_TYPE,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Type
                    </TableCell>
                    {/* Period columns */}
                    {periods.map(period => (
                      <TableCell
                        key={period}
                        align="center"
                        sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 3,
                          backgroundColor: '#42B0D5',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.78rem',
                          borderRight: BORDER_MEDIUM,
                          minWidth: 90,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatPeriodLabel(period, periodType)}
                      </TableCell>
                    ))}
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedTab === 'p1-p2' ? (
                p1p2Periods.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={p1p2Periods.length + 3} align="center" sx={{ py: 6, color: '#999' }}>
                      No data available for selected filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  renderP1P2Rows()
                )
              ) : viewBy === 'platform' ? (
                allUniquePlatforms.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={periods.length + 3} align="center" sx={{ py: 6, color: '#999' }}>
                      No data available for selected filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  renderPlatformOnlyRows()
                )
              ) : (
                portfolios.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={periods.length + 3} align="center" sx={{ py: 6, color: '#999' }}>
                      No data available for selected filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  renderPortfolioRows()
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default AssetChangeMetrics;
